import React, { useState, useContext, useEffect } from 'react';
import { trackLogin } from '../utils/analytics';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
    MailIcon as Mail,
    LockIcon as Lock,
    EyeIcon as Eye,
    EyeOffIcon as EyeOff,
    CheckCircleIcon as CheckCircle2,
    AlertCircleIcon as AlertCircle,
    ArrowRightIcon as ArrowRight
} from '../components/Icons';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const { login, googleLogin, API_URL, PUBLIC_API_URL } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Handle Google Auth redirect
        const queryParams = new URLSearchParams(location.search);
        const tokenData = queryParams.get('token');
        const errorMsg = queryParams.get('error');

        if (tokenData) {
            try {
                let jwtToken = tokenData;
                try {
                    const parsed = JSON.parse(decodeURIComponent(tokenData));
                    if (parsed && typeof parsed === 'object' && parsed.token) {
                        jwtToken = parsed.token;
                    }
                } catch (e) {
                    // tokenData is already raw JWT
                }
                
                // Sync with AuthContext
                if (googleLogin) {
                    googleLogin(jwtToken);
                } else {
                    localStorage.setItem('user', JSON.stringify({ token: jwtToken }));
                }
                
                setShowSuccess(true);
                trackLogin('google');
                setTimeout(() => navigate('/'), 1500);
            } catch (e) {
                console.error('Error parsing token data:', e);
                setError('Anmeldung mit Google fehlgeschlagen.');
            }
        }

        if (errorMsg) {
            setError(errorMsg);
        }

        if (location.state?.email) {
            setEmail(location.state.email);
        }
        if (location.state?.message) {
            setError(location.state.message);
        }
    }, [location, navigate, googleLogin]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await login(email, password);

            trackLogin('email');
            setShowSuccess(true);

            setTimeout(() => {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                if (storedUser && (storedUser.role === 'gruppenleiter' || storedUser.role === 'admin')) {
                    const hasProfilePhoto = !!(storedUser.userData?.profilePhoto?.url || storedUser.userData?.avatarConfig?.modelUrl);
                    if (!hasProfilePhoto) {
                        navigate('/profile');
                        return;
                    }
                }
                navigate('/');
            }, 1500);
        } catch (error) {
            console.error('Login failed:', error);
            setError(error.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Daten.');
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-surface flex flex-col overflow-x-hidden">
            <main className="flex-1 flex items-center justify-center p-6 mt-16 relative">
                {/* Decorative background elements */}
                <div className="absolute top-1/4 -left-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10 animate-pulse"></div>
                <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-tertiary/5 rounded-full blur-3xl -z-10 animate-pulse delay-1000"></div>

                <div className="w-full max-w-md">
                    <div className="md3-card-elevated bg-surface-containerHigh p-8 md:p-10 rounded-[2.5rem] shadow-elevation-3 transition-all duration-medium2">
                        {/* Header */}
                        <div className="text-center mb-10">
                            <div className="relative w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                                <div className="absolute inset-0 rounded-2xl bg-primary opacity-10 rotate-12"></div>
                                <Lock className="text-primary" size={32} />
                            </div>

                            <h1 className="text-headline-medium font-bold text-surface-on mb-2">
                                Dashboard Login
                            </h1>
                            <p className="text-body-large text-surface-onVariant">
                                Melde dich an, um dein Team zu verwalten
                            </p>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="mb-8 p-4 bg-error-container text-error-onContainer rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-medium2">
                                <AlertCircle size={20} className="mt-0.5 shrink-0" />
                                <p className="text-label-large">{error}</p>
                            </div>
                        )}

                        {/* Success Message */}
                        {showSuccess ? (
                            <div className="text-center py-10 animate-in zoom-in duration-medium2">
                                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                                    <CheckCircle2 className="text-primary" size={48} />
                                </div>
                                <h2 className="text-headline-small font-bold text-surface-on mb-2">
                                    Erfolgreich!
                                </h2>
                                <p className="text-body-medium text-surface-onVariant animate-pulse">
                                    Du wirst zum Dashboard weitergeleitet...
                                </p>
                            </div>
                        ) : (
                            /* Form */
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Email Input */}
                                <div className="space-y-1">
                                    <label htmlFor="email" className="text-label-medium text-surface-onVariant ml-1">
                                        E-Mail Adresse
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-surface-onVariant group-focus-within:text-primary transition-colors">
                                            <Mail size={20} />
                                        </div>
                                        <input
                                            id="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className="w-full h-14 pl-12 pr-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                                            placeholder="admin@pfadi.de"
                                            autoComplete="email"
                                        />
                                    </div>
                                </div>

                                {/* Password Input */}
                                <div className="space-y-1">
                                    <div className="flex justify-between items-center ml-1">
                                        <label htmlFor="password" className="text-label-medium text-surface-onVariant">
                                            Passwort
                                        </label>
                                        <Link
                                            to="/forgotpassword"
                                            className="text-label-small text-primary hover:underline transition-all"
                                        >
                                            Passwort vergessen?
                                        </Link>
                                    </div>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-surface-onVariant group-focus-within:text-primary transition-colors">
                                            <Lock size={20} />
                                        </div>
                                        <input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="w-full h-14 pl-12 pr-12 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                                            placeholder="••••••••"
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-4 flex items-center text-surface-onVariant hover:text-surface-on transition-colors"
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="md3-btn-filled w-full h-14 rounded-full text-title-medium shadow-elevation-1 hover:shadow-elevation-2 active:scale-95 transition-all flex items-center justify-center gap-2 group"
                                >
                                    {isLoading ? (
                                        <div className="w-6 h-6 border-2 border-surface/30 border-t-surface rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            Anmelden <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>

                                {/* Divider */}
                                <div className="relative py-4">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-outlineVariant"></div>
                                    </div>
                                    <div className="relative flex justify-center text-label-medium">
                                        <span className="bg-surface-containerHigh px-4 text-surface-onVariant">ODER</span>
                                    </div>
                                </div>

                                {/* Google Login Button */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        trackLogin('google');
                                        window.location.href = `${PUBLIC_API_URL}/auth/google`;
                                    }}
                                    className="md3-btn-outlined w-full h-14 rounded-full border-2 border-outline hover:bg-surface-containerHighest transition-all flex items-center justify-center gap-3 text-title-medium hover:border-primary/50"
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 6.35l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Mit Google anmelden
                                </button>

                                {/* Signup Link */}
                                <div className="text-center pt-4">
                                    <p className="text-body-medium text-surface-onVariant">
                                        Noch kein Konto?{' '}
                                        <Link
                                            to="/register"
                                            className="text-primary font-bold hover:underline transition-all"
                                        >
                                            Registrieren
                                        </Link>
                                    </p>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Login;
